<template>
  <div class="my-page-new-activities" ref="newActivitiesPane">
    <hl-ModalV2
      v-if="duplicateDepositWarning.shouldShow"
      :title="duplicateDepositWarningTitle"
      titleIcon="budicon-alert-sign"
      is-closable
      data-cy="ddg-important-information-modal"
      @close="duplicateDepositWarning.shouldShow = false"
    >
      <a-RenderHTML :value="duplicateDepositWarningText" />

      <template v-slot:buttons>
        <hl-Button variant="extra" @click="abortDepositCreation">
          {{ translate('myPage.newActivities.dupDepositWarning.buttons.cancel') }}
        </hl-Button>

        <hl-Button variant="primary" class="ml-2" @click="goToDepositAccountApp(context)">
          {{ translate('myPage.newActivities.dupDepositWarning.buttons.continue') }}
        </hl-Button>
      </template>
    </hl-ModalV2>

    <transition name="my-page-new-activities__slidey" mode="out-in">
      <MyPageContextWizard
        v-if="currentWizard"
        class="my-page-new-activities__activity"
        :key="currentWizard"
        :activity="currentWizard"
        :inputs="inputs"
        :primary-action="primaryAction"
        :secondary-action="secondaryAction"
        @change="context = $event"
        @click:cancel="currentWizard = undefined"
        @click:primaryAction="performPrimaryAction"
        @click:secondaryAction="performSecondaryAction"
      />

      <div v-else key="buttons">
        <div class="my-page-new-activities__buttons">
          <hl-ButtonTapWithIcon
            v-for="button in getButtons"
            layout="vertical"
            :iconClass="button.iconClass"
            class="my-page-new-activity"
            @click.native="startActivity(...button.clickEventParams)"
            :key="button.label"
          >
            <p v-if="button.actionLabel">
              {{ button.actionLabel }}
            </p>
            <span class="font-weight-bold">
              {{ button.label }}
            </span>
          </hl-ButtonTapWithIcon>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MyPageContextWizard from '@/components/mypage/MyPageContextWizard';
import RenderHTML from '@modules/design-system/components/atoms/RenderHTML.vue';
import ButtonTapWithIcon from '@modules/husleie-framework/src/components/buttons/ButtonTapWithIcon.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import { LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import i18nCommon from '@modules/i18n/mixins/Common';
import Raven from 'raven-js';
import ModalV2 from '@modules/husleie-framework/src/components/modals/ModalV2.vue';
import { ProfilesLandlordApi, InspectionReportApi, RentalObjectApi } from '@modules/husleieapi2';
import { getDefaultFlatStructure } from '@modules/husleie-framework/src/helpers/inspectionReport';

const profilesLandlordApi = new ProfilesLandlordApi();
const inspectionReportApi = new InspectionReportApi();
const rentalObjectApi = new RentalObjectApi();

export default {
  name: 'MyPageNewActivities',
  mixins: [i18nCommon],
  components: {
    MyPageContextWizard,
    'hl-ButtonTapWithIcon': ButtonTapWithIcon,
    'hl-ModalV2': ModalV2,
    'a-RenderHTML': RenderHTML,
  },
  props: {
    showMoveoutProtocolButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      context: {
        landlordId: undefined,
        rentalObjectId: undefined,
        tenancyType: undefined,
        tenancyId: undefined,
      },
      currentWizard: undefined,
      localTenancyCopy: {},
      localLandlordCopy: undefined,
      localRentalObjectCopy: undefined,
      duplicateDepositWarning: {
        shouldShow: false,
        type: undefined,
      },
    };
  },

  computed: {
    ...mapGetters(['getLandlordProfilesMeta', 'getUserProfile']),
    ...mapGetters('tenancy', ['getTenancy', 'getTenancyDeposits']),

    duplicateDepositWarningTitle() {
      switch (this.duplicateDepositWarning.type) {
        case 'Garantert':
          return this.translate('myPage.newActivities.dupDepositWarning.ddg.title');
        case 'deposit_instabank':
        case 'deposit_leabank':
          return this.translate('myPage.newActivities.dupDepositWarning.account.title');
        case 'deposit_guarantee':
        case 'upsell_deposit_subscription':
          return this.translate('myPage.newActivities.dupDepositWarning.subscription.title');
        default:
          return '';
      }
    },
    duplicateDepositWarningText() {
      switch (this.duplicateDepositWarning.type) {
        case 'Garantert':
          return this.translate('myPage.newActivities.dupDepositWarning.ddg.md-text');
        case 'deposit_instabank':
        case 'deposit_leabank':
          return this.translate('myPage.newActivities.dupDepositWarning.account.md-text');
        case 'deposit_guarantee':
        case 'upsell_deposit_subscription':
          return this.translate('myPage.newActivities.dupDepositWarning.subscription.md-text');
        default:
          return '';
      }
    },
    getWizard() {
      return {
        newRentalObject: {
          inputs: {},
          primaryAction: {
            buttonEnabled: this.context.landlordId,
            buttonText: 'Opprett leieobjekt',
            method: this.startNewRentalObject,
          },
        },
        pubomatic: {
          inputs: {
            isRentalObjectVisible: true,
          },
          primaryAction: {
            buttonEnabled: this.context.landlordId && this.context.rentalObjectId,
            buttonText: this.translationStartCreateAdvertWizard,
            method: this.startPubomatic,
          },
        },
        contract: {
          inputs: {
            isRentalObjectVisible: true,
          },
          primaryAction: {
            buttonEnabled: this.context.landlordId && this.context.rentalObjectId,
            buttonText: 'Opprett leiekontrakt',
            method: this.startContract,
          },
        },
        addendum: {
          inputs: {
            isRentalObjectVisible: true,
            isTenancyVisible: true,
          },
          primaryAction: {
            buttonEnabled: this.context.landlordId && this.context.rentalObjectId && this.context.tenancyId,
            buttonText: 'Opprett tilleggskontrakt',
            method: this.startAddendum,
          },
        },
        depositAccount: {
          inputs: {
            isRentalObjectVisible: true,
            isTenancyTypeVisible: true,
            isTenancyVisible: this.context.tenancyType === 'existing',
          },
          primaryAction: {
            buttonEnabled:
              this.context.landlordId &&
              this.context.rentalObjectId &&
              this.context.tenancyType &&
              (this.context.tenancyType === 'new' || this.context.tenancyId),
            buttonText: 'Bestill depositumskonto',
            method: this.startDepositAccount,
            type: 'depositAccount',
          },
        },
        rentalObject: {
          inputs: {
            isRentalObjectVisible: true,
          },
          primaryAction: {
            buttonEnabled: this.context.landlordId && this.context.rentalObjectId,
            buttonText: this.translationStarCreateTenancyWizard,
            method: this.startNewTenancy,
          },
        },
        top: {
          inputs: {
            isRentalObjectVisible: true,
            isTenancyVisible: true,
          },
          primaryAction: {
            buttonEnabled: this.context.landlordId && this.context.rentalObjectId,
            buttonText: 'Opprett overtakelsesprotokoll',
            method: this.startTop,
          },
        },
        termination: {
          inputs: {
            isRentalObjectVisible: true,
            isTenancyTypeVisible: true,
            isTenancyVisible: this.context.tenancyType === 'existing',
          },
          primaryAction: {
            buttonEnabled:
              this.context.landlordId &&
              this.context.rentalObjectId &&
              this.context.tenancyType &&
              (this.context.tenancyType === 'new' || this.context.tenancyId),
            buttonText: this.translationStartTenancyTerminationWizard,
            method: this.tenancyTermination,
          },
          secondaryAction: {
            buttonEnabled:
              this.context.landlordId &&
              this.context.rentalObjectId &&
              this.context.tenancyType &&
              this.context.tenancyId,
            buttonText: this.translationLeaseTerminationSelectEndDate,
            method: this.openSetTenancyEndDate,
          },
        },
        moveout: {
          inputs: {
            isRentalObjectVisible: true,
            isTenancyVisible: true,
          },
          primaryAction: {
            buttonEnabled:
              this.context.landlordId &&
              this.context.rentalObjectId &&
              this.context.tenancyId &&
              this.showMoveoutProtocolButton,
            buttonText: 'Opprett utflyttingsprotokoll',
            method: this.startMoveout,
          },
        },
      };
    },
    getButtons() {
      const buttons = [
        {
          iconClass: 'budicon-house-a',
          clickEventParams: ['newRentalObject', this.translate('myPage.wizard.Create rental object')],
          actionLabel: this.translate('myPage.wizard.Create'),
          label: this.translate('myPage.wizard.Rental object'),
        },
        {
          iconClass: 'budicon-announcement',
          clickEventParams: ['pubomatic', this.translate('myPage.wizard.Create ad')],
          actionLabel: this.translate('myPage.wizard.Create'),
          label: this.translate('myPage.wizard.ad'),
        },
        {
          iconClass: 'budicon-document-page',
          clickEventParams: ['contract', this.translate('myPage.wizard.Create tenancy')],
          actionLabel: this.translate('myPage.wizard.Create'),
          label: this.translate('myPage.wizard.Tenancy'),
        },
        {
          iconClass: 'budicon-doc-attachment',
          clickEventParams: ['addendum', 'Opprett tilleggskontrakt'],
          actionLabel: this.translate('myPage.wizard.Create'),
          label: 'Tilleggskontrakt',
        },
        {
          iconClass: 'budicon-bank',
          clickEventParams: ['depositAccount', this.translate('myPage.wizard.Order deposit account')],
          actionLabel: this.translate('myPage.wizard.Order'),
          label: this.translate('myPage.wizard.Deposit account'),
        },
        {
          iconClass: 'budicon-deal',
          clickEventParams: ['rentalObject', this.translate('myPage.wizard.Create pre-existing tenancies')],
          actionLabel: this.translate('myPage.wizard.Create'),
          label: this.translate('myPage.wizard.Pre-existing tenancies'),
        },
        {
          iconClass: 'budicon-ui-compose',
          clickEventParams: ['top', this.translate('myPage.wizard.Create takeover protocol')],
          actionLabel: this.translate('myPage.wizard.Create'),
          label: this.translate('myPage.wizard.Takeover protocol'),
        },
        {
          iconClass: 'budicon-open-door',
          clickEventParams: ['termination', this.translate('myPage.wizard.Begin termination')],
          actionLabel: this.translate('myPage.wizard.Begin'),
          label: this.translate('myPage.wizard.Termination'),
        },
      ];

      if (this.showMoveoutProtocolButton) {
        buttons.push({
          iconClass: 'budicon-ui-compose',
          clickEventParams: ['moveout', 'Opprett utflyttingsprotokoll'],
          actionLabel: 'Opprett',
          label: 'Utflyttingsprotokoll',
        });
      }

      return buttons;
    },
    inputs() {
      return this.getWizard[this.currentWizard].inputs;
    },
    primaryAction() {
      return this.getWizard[this.currentWizard].primaryAction;
    },
    secondaryAction() {
      return this.getWizard[this.currentWizard].secondaryAction;
    },
    translationTerminationEnding() {
      return this.translate('termination and ending');
    },
    translationStartCreateAdvertWizard() {
      return this.translate('myPage.wizard.start create advert wizard');
    },
    translationStarCreateTenancyWizard() {
      return this.translate('myPage.wizard.start create tenancy wizard');
    },
    translationStartTenancyTerminationWizard() {
      return this.translate('contextWizzard.leaseTermination.createDocument');
    },
    translationLeaseTerminationSelectEndDate() {
      return this.translate('contextWizzard.leaseTermination.selectEndDate');
    },
    translationNewRentalProperty() {
      return this.translate('myPage.wizard.new rental property');
    },
    translationNewFinnAdvert() {
      return this.translate('myPage.wizard.new FINN advert');
    },
    translationNewFinnAdvertSlot() {
      return this.translate('myPage.wizard.md-new FINN advert');
    },
    translationNewLease() {
      return this.translate('myPage.wizard.new lease');
    },
    translationNewLeaseSlot() {
      return this.translate('myPage.wizard.md-new lease');
    },
    translationNewDepositAccount() {
      return this.translate('myPage.wizard.new deposit account');
    },
    translationNewDepositAccountSlot() {
      return this.translate('myPage.wizard.md-new deposit account');
    },
    translationManageExistingLeases() {
      return this.translate('myPage.wizard.manage existing leases');
    },
    translationManageExistingLeasesSlot() {
      return this.translate('myPage.wizard.md-manage existing leases');
    },
    translationNewOvertakeProtocol() {
      return this.translate('myPage.wizard.new overtake protocol');
    },
    translationNewOvertakeProtocolSlot() {
      return this.translate('myPage.wizard.md-new overtake protocol');
    },
    translationTerminationEndingSlot() {
      return this.translate('myPage.wizard.md-termination and ending');
    },
  },
  watch: {
    currentWizard(newValue) {
      if (!newValue) {
        this.$emit('activityDone');
      }
      const pane = this.$refs.newActivitiesPane;
      pane && pane.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  },
  methods: {
    ...mapActions('tenancy', ['fetchTenancy', 'fetchTenancyDeposits']),

    parseFullName(fullName) {
      if (!fullName || typeof fullName !== 'string') {
        return { givenName: '', familyName: '' };
      }
      const lastSpaceIndex = fullName.lastIndexOf(' ');
      const givenName = lastSpaceIndex > -1 ? fullName.substring(0, lastSpaceIndex) : fullName;
      const familyName = lastSpaceIndex > -1 ? fullName.substring(lastSpaceIndex + 1) : '';
      return { givenName, familyName };
    },

    async fetchTenancyDetails(landlordId, tenancyId) {
      try {
        this.localTenancyCopy = await this.fetchTenancy({
          landlordId,
          tenancyId,
        });
      } catch (error) {
        Raven.captureException(error);
        // eslint-disable-next-line
        console.error(error);
      }
    },
    performPrimaryAction(context) {
      if (this.getWizard[this.currentWizard].primaryAction?.method) {
        this.getWizard[this.currentWizard].primaryAction.method(context);
      }
    },
    performSecondaryAction(context) {
      if (this.getWizard[this.currentWizard].secondaryAction?.method) {
        this.getWizard[this.currentWizard].secondaryAction.method(context);
      }
    },
    startActivity(key, title) {
      this.currentWizard = key;
      this.$emit('activityInitiated', {
        key,
        title,
      });
    },
    startNewRentalObject({ landlordId }) {
      this.$router.push({ name: 'newRO', params: { landlordId } });
      this.currentWizard = undefined;
    },
    startPubomatic({ landlordId, rentalObjectId }) {
      RedirectOperations.goToURL(
        AppRedirect.AD_CREATE,
        {
          landlordId,
          rentalObjectId,
        },
        true
      );
      this.currentWizard = undefined;
    },
    startContract({ rentalObjectId, tenancyId }) {
      RedirectOperations.goToURL(AppRedirect.CONTRACT_CREATE, { rentalObjectId, tenancyId }, true);
      this.currentWizard = undefined;
    },
    async goToDepositAccountApp({ landlordId, rentalObjectId, tenancyId }) {
      const landlordMeta = this.getLandlordProfilesMeta.find(({ id }) => id === landlordId);
      const isCompany = landlordMeta.meta && landlordMeta.organizationNumber;
      const tenant = tenancyId ? this.getTenancy.tenants.find(({ type }) => type === 'tenant') : undefined;

      const redirectObject = {
        landlordId,
        landlordName: landlordMeta.fullName,
        landlordType: LandlordTypeEnum.PRIVATE,
        rentalObjectId,
        tenancyId,
        ...(tenancyId
          ? {
              tenantName: tenant.fullName,
              tenantEmail: tenant.email,
              tenantCellphone: tenant.cellPhone,
              rentRent: this.getTenancy.rent.rent,
              rentDuration: this.getTenancy.endDate !== null ? 'limited' : 'unlimited',
              rentStartdate: this.getTenancy.startDate,
              rentEnddate: this.getTenancy.endDate,
            }
          : {}),
        ...(isCompany
          ? {
              landlordType: LandlordTypeEnum.PROFESSIONAL,
              companyName: landlordMeta.fullName,
              companyOrgNumber: landlordMeta.organizationNumber,
            }
          : {}),
      };

      RedirectOperations.goToURL(AppRedirect.DEPOSIT_ACCOUNT, redirectObject, true);

      this.currentWizard = undefined;
      this.duplicateDepositWarning = {
        shouldShow: false,
        type: undefined,
      };
    },
    async startDepositAccount({ landlordId, rentalObjectId, tenancyId }) {
      if (tenancyId) {
        await this.fetchTenancyDetails(landlordId, tenancyId);

        await this.fetchTenancyDeposits({
          landlordId: landlordId,
          tenancyId: tenancyId,
        });

        const depositTypes = [
          'deposit_guarantee',
          'deposit_instabank',
          'deposit_leabank',
          'upsell_deposit_subscription',
        ];

        const deposit = this.getTenancyDeposits.find(deposit => depositTypes.includes(deposit.category));

        if (this.getTenancy.subscription.subscriptionPlanName === 'Garantert') {
          this.duplicateDepositWarning = {
            shouldShow: true,
            type: 'Garantert',
          };
          return;
        }

        if (deposit) {
          this.duplicateDepositWarning = {
            shouldShow: true,
            type: deposit.category,
          };
          return;
        }
      }

      this.goToDepositAccountApp({
        landlordId,
        rentalObjectId,
        tenancyId,
      });
    },
    abortDepositCreation() {
      this.duplicateDepositWarning = {
        shouldShow: false,
        type: undefined,
      };
    },
    startNewTenancy({ rentalObjectId, landlordId }) {
      this.$router.push({
        name: 'rentalObject_new_tenancy',
        params: { rentalObjectId, landlordId },
      });
      this.currentWizard = undefined;
    },
    async fetchInspectionData(landlordId, rentalObjectId, tenancyId) {
      this.localLandlordCopy = await profilesLandlordApi.getProfileLandlord(landlordId);

      if (tenancyId && tenancyId != 0) {
        await this.fetchTenancyDetails(landlordId, tenancyId);
        if (this.localTenancyCopy.rentalObjectId) {
          this.localRentalObjectCopy = await rentalObjectApi.getLandlordRentalObject(
            landlordId,
            this.localTenancyCopy.rentalObjectId
          );
        }
      }

      if (!this.localRentalObjectCopy && rentalObjectId && rentalObjectId != 0) {
        this.localRentalObjectCopy = await rentalObjectApi.getLandlordRentalObject(landlordId, rentalObjectId);
      }
    },
    async startTop({ landlordId, rentalObjectId, tenancyId }) {
      if (!rentalObjectId) {
        this.$toast('Velg et leieobjekt.', 'error');
        return;
      }
      if (!tenancyId) {
        this.$toast('Velg et leieforhold.', 'error');
        return;
      }
      try {
        await this.fetchInspectionData(landlordId, rentalObjectId, tenancyId);

        const data = this.getInitialInspectionReportData('takeover');
        const newInspectionReport = await inspectionReportApi.createInspectionReport({
          body: {
            landlordId,
            tenancyId,
            rentalObjectId,
            data: data,
            status: 'draft',
          },
        });

        window.open(`/dashboard/landlord/${landlordId}/tenancy/${tenancyId}/object/${rentalObjectId}/inspection-report/${newInspectionReport.id}`, '_blank');
        this.currentWizard = undefined;
      } catch (error) {
        Raven.captureException(error);
        console.error('Failed to create inspection report:', error);
      }
    },
    getInitialInspectionReportData(reportType = 'takeover') {
      const data = getDefaultFlatStructure(reportType);

      // For moveout reports, return minimal structure without prefilling
      if (reportType === 'moveout') {
        // Prefill here if any
        return data;
      }

      // For takeover reports, continue with prefilling logic

      const landlord = this.localLandlordCopy;
      // Prefill landlord details
      if (landlord) {
        const { givenName, familyName } = this.parseFullName(landlord.owner.name || '');

        data.landlord_given_name = givenName;
        data.landlord_family_name = familyName;
        data.landlord_cell_phone = landlord?.cellPhone || '';

        let ll_address;
        if (landlord.addresses && Array.isArray(landlord.addresses)) {
          ll_address = landlord.addresses.find(addr => addr.purpose === 'postal');
        }
        if (ll_address) {
          data.landlord_email = ll_address.email || '';
        } else {
          data.landlord_email = this.getUserProfile.email;
        }

        data.landlord_email = ll_address.email;
      }

      const tenancy = this.localTenancyCopy;

      // Prefill tenancy details
      if (tenancy && Object.keys(tenancy).length != 0) {
        data.tenancy_due_date = tenancy.rent.dueDay;
        data.tenancy_due_period = tenancy.rent.duePeriod;
        if (!tenancy.endDate) {
          data.tenancy_unlimited = true;
        } else {
          data.tenancy_end_date = tenancy.endDate;
        }

        data.tenancy_rent = tenancy.rent.rent;
        data.tenancy_start_date = tenancy.startDate;
        if (tenancy.hasDeposits) {
          // Assumed there only one deposit for each tenancy... Why is this an object with deposit ids as keys :screaming:
          for (const [key, value] of Object.entries(tenancy.deposits)) {
            if (value.category) {
              data.tenancy_deposit = value.category;
              break;
            }
          }
        }

        // Prefill tenant details
        if (tenancy?.tenants.length > 0) {
          const tenant = tenancy.tenants[0];
          const t_fullName = tenant.fullName;
          const t_allNames = t_fullName.trim().split(/\s+/);
          const t_familyName = t_allNames.pop();
          const t_givenName = t_allNames.join(' ');

          data.tenant_given_name = t_givenName;
          data.tenant_family_name = t_familyName;
          data.tenant_email = tenant.email;
          data.tenant_cell_phone = tenant.cellPhone;
        }
      }

      const rentalObject = this.localRentalObjectCopy;
      // Prefill rental-object details
      if (rentalObject) {
        data.rental_object_street = rentalObject.address.street;
        data.rental_object_postal_zipcode = rentalObject.address.postalZipcode;
        data.rental_object_postal_area = rentalObject.address.postalArea;
        data.rental_object_apartment_number = rentalObject.details.apartmentNumber;
        data.rental_object_type = rentalObject.details.type;

        // I expect roomNumer "0" is the same as ""
        if (rentalObject.details.roomNumber != '0') {
          data.rental_object_room_number = rentalObject.details.roomNumber;
        }
      }
      return data;
    },
    openSetTenancyEndDate() {
      const settingsPath = `rentalObject/${this.context.rentalObjectId}/tenancy/${this.context.tenancyId}/settings`;
      window.location.href =
        RedirectOperations.getURL(AppRedirect.DASHBOARD_LANDLORD, {
          landlordId: this.context.landlordId,
        }) + settingsPath;
    },
    tenancyTermination() {
      RedirectOperations.goToURL(
        AppRedirect.TENANCY_TERMINATION,
        {
          rentalObjectId: this.context.rentalObjectId,
          tenancyId: this.context.tenancyType === 'new' ? undefined : this.context.tenancyId,
        },
        true
      );

      this.currentWizard = undefined;
    },
    startAddendum() {
      RedirectOperations.goToURL(
        AppRedirect.ADDENDUM_LANDLORD,
        {
          tenancyId: this.context.tenancyId,
        },
        true,
      );

      this.currentWizard = undefined;
    },
    async startMoveout({ landlordId, rentalObjectId, tenancyId = 0, tenancyType }) {
      if (!rentalObjectId) {
        this.$toast('Velg et leieobjekt.', 'error');
        return;
      }
      if (tenancyType === 'existing' && tenancyId === 0) {
        this.$toast('Velg et leieforhold.', 'error');
        return;
      }
      try {
        await this.fetchInspectionData(landlordId, rentalObjectId, tenancyId);

        const data = this.getInitialInspectionReportData('moveout');

        // Add landlord details
        const landlord = this.localLandlordCopy;
        if (landlord) {
          const { givenName, familyName } = this.parseFullName(landlord.owner.name || '');

          data.landlord_given_name = givenName;
          data.landlord_family_name = familyName;

          let landlordEmail = '';
          if (landlord.addresses && Array.isArray(landlord.addresses)) {
            const postalAddress = landlord.addresses.find(addr => addr.purpose === 'postal');
            landlordEmail = postalAddress?.email || '';
          }
          if (!landlordEmail) {
            landlordEmail = this.getUserProfile.email || '';
          }
          data.landlord_email = landlordEmail;
        }

        // Add rental-object details
        if (this.localRentalObjectCopy?.address) {
          data.rental_object_street = this.localRentalObjectCopy.address.street;
          data.rental_object_postal_area = this.localRentalObjectCopy.address.postalArea;
        }

        // Only prefill tenancy-related data if tenancyType is "existing"
        if (tenancyType === 'existing') {
          if (this.localTenancyCopy.hasDeposits && this.localTenancyCopy.deposits) {
            data.deposits = this.localTenancyCopy.deposits;
          }

          const tenant = this.localTenancyCopy?.tenants?.[0];
          const { givenName, familyName } = this.parseFullName(tenant?.fullName || '');
          data.tenant_given_name = givenName;
          data.tenant_family_name = familyName;
          data.tenant_email = tenant?.email || '';
        }

        const newInspectionReport = await inspectionReportApi.createInspectionReport({
          body: {
            landlordId,
            tenancyId,
            rentalObjectId,
            data: data,
            status: 'draft',
          }
        });

        window.open(`/dashboard/landlord/${landlordId}/tenancy/${tenancyId}/object/${rentalObjectId}/inspection-report/${newInspectionReport.id}`, '_blank');
        this.currentWizard = undefined;
      } catch (error) {
        Raven.captureException(error);
        console.error('Failed to create moveout inspection report:', error);
      }
    },
  },
};
</script>

<style>
.my-page-new-activities {
  @apply mt-4 overflow-hidden;
}

.my-page-new-activities__activity {
  @apply flex h-full flex-col;
}

.my-page-new-activities__buttons {
  @apply grid gap-5 text-center;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

@media (min-width: 800px) {
  .my-page-new-activities__buttons {
    @apply grid gap-5 text-center;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    grid-auto-rows: 1fr;
  }
}

.my-page-new-activities__slidey-enter-active,
.my-page-new-activities__slidey-leave-active {
  transition: 300ms;
}

.my-page-new-activities__slidey-enter-to,
.my-page-new-activities__slidey-leave {
  opacity: 1;
}

.my-page-new-activities__slidey-enter {
  transform: translate(100%, 0);
  opacity: 0;
}

.my-page-new-activities__slidey-leave-to {
  transform: translate(200%, 0);
}
</style>
